import React, { useState, useEffect } from 'react';
import { Bootpay } from '@bootpay/client-js';
import axios from 'utils/axiosInstance';
import getRootData from 'utils/getRootData';
import { UserInfo } from 'types/user_info';
import MemberNavigation from 'components/organs/member/MemberNavigation';

const VITE_BOOTPAY_JAVASCRIPT_KEY = import.meta.env.VITE_BOOTPAY_JAVASCRIPT_KEY;

interface RootData {
  userInfo: UserInfo;
  payment_data?: {
    active_subscription: Subscription | null;
    billing_key: BillingKey | null;
    payment_methods: BillingKey[];
  };
}

interface BillingKey {
  id: number;
  billing_key: string;
  payment_method?: string;
  pg?: string;
  last_used_at?: string;
  status?: string;
  active: boolean;
  created_at: string;
  card_no_masked?: string;
  card_company?: string;
  card_type?: string;
  raw_data?: {
    card_company?: string;
    card_no?: string;
    verified?: {
      card_data?: {
        card_no?: string;
        card_company?: string;
        card_type?: string;
      };
    };
    [key: string]: any;
  };
}

interface Subscription {
  id: number;
  subscription_status: string;
  amount?: number;
  cycle?: string;
  service_name?: string;
  next_payment_date?: string;
}

interface PaymentMethodData {
  active_subscription: Subscription | null;
  billing_key: BillingKey | null;
  payment_methods: BillingKey[];
}

export default function PaymentMethodManagement() {
  const { userInfo } = getRootData<RootData>() ?? { userInfo: null };
  const roleInfo = userInfo?.roleinfo || {};
  const isMajorAdmin = Boolean(roleInfo.isMajorAdminGroup ?? roleInfo.is_major_admin_group);
  const [data, setData] = useState<PaymentMethodData | null>(null);
  const [loading, setLoading] = useState(true);
  const [isChanging, setIsChanging] = useState(false);
  const [processingStep, setProcessingStep] = useState('');

  useEffect(() => {
    if (!userInfo) {
      alert('로그인이 필요합니다.');
      window.location.href = '/users/sign_in?return_to=/payment/payment_methods';
      return;
    }

    fetchPaymentMethodData();
  }, []);

  const fetchPaymentMethodData = async () => {
    try {
      // getRootData에서 presenter 데이터 가져오기
      const rootData = getRootData<RootData>();
      if (rootData?.payment_data) {
        setData({
          active_subscription: rootData.payment_data.active_subscription,
          billing_key: rootData.payment_data.billing_key,
          payment_methods: rootData.payment_data.payment_methods
        });
        console.log('Payment method data loaded from presenter:', rootData.payment_data);
      } else {
        // fallback to API call
        const response = await axios.get('/payment/payment_methods.json');
        if (response.data.success) {
          setData(response.data.data);
          console.log('Payment method data loaded from API:', response.data.data);
        } else {
          console.error('Failed to fetch payment method data:', response.data.message);
          setData({ active_subscription: null, billing_key: null, payment_methods: [] });
        }
      }
    } catch (error) {
      console.error('Failed to fetch payment method data:', error);
      // 에러 발생시 빈 데이터로 설정
      setData({ active_subscription: null, billing_key: null, payment_methods: [] });
    } finally {
      setLoading(false);
    }
  };

  const handleChangePaymentMethod = async () => {
    if (!userInfo || !data?.active_subscription) {
      alert('구독 정보를 찾을 수 없습니다.');
      return;
    }

    setIsChanging(true);
    setProcessingStep('결제수단 변경 준비 중...');

    try {
      // 1. 결제수단 변경 요청 준비
      const response = await axios.post('/payment/change_payment_method');
      const prepareData = response.data;

      console.log('Payment method change preparation response:', prepareData);

      if (!prepareData.success) {
        alert(prepareData.message || '결제수단 변경 준비에 실패했습니다.');
        return;
      }

      setProcessingStep('새로운 결제수단으로 테스트 결제 중...');

      // 2. Bootpay 구독 결제 실행 (결제수단 변경용)
      const bootpayResponse = await Bootpay.requestSubscription({
        application_id: VITE_BOOTPAY_JAVASCRIPT_KEY,
        pg: 'nicepay',
        order_name: '결제수단 변경',
        subscription_id: prepareData.order_id, // 백엔드에서 생성된 order_id 사용
        price: prepareData.amount,
        tax_free: prepareData.amount,
        user: {
          id: userInfo.id?.toString(),
          username: userInfo.name,
          email: userInfo.email,
        },
        extra: {
          subscribe_test_payment: true, // 테스트 결제
          open_type: 'iframe',
          original_subscription_id: data.active_subscription?.id,
          display_error_result: true, // 결제 실패 이탈을 방지하는 팝업 노출
        }
      });

      if (bootpayResponse.event === 'done') {
        setProcessingStep('결제수단 변경 처리 중...');
        // 3. 결제 완료 후 상태 확인 - order_id 전달
        await checkPaymentMethodChangeStatus(prepareData.payment_request_id, prepareData.order_id);
      } else {
        alert('결제가 취소되었습니다.');
      }

    } catch (error: any) {
      console.error('Payment method change error:', error);
      if(error.event == 'cancel') {
        alert(error.message);
      } else {
        alert('결제수단 변경 중 오류가 발생했습니다.');
      }
    } finally {
      setIsChanging(false);
      setProcessingStep('');
    }
  };

  const checkPaymentMethodChangeStatus = async (paymentRequestId: number, orderId?: string) => {
    let attempts = 0;
    const maxAttempts = 60; // 최대 60초 대기 (증가)

    const pollStatus = async (): Promise<void> => {
      attempts++;
      console.log(`Payment method change polling attempt ${attempts}/${maxAttempts} for payment_request_id: ${paymentRequestId}${orderId ? `, order_id: ${orderId}` : ''}`);

      try {
        const response = await axios.get(`/payment/change_method_status/${paymentRequestId}`);
        const statusData = response.data;

        console.log('Payment method change status response:', statusData);

        if (statusData.success && statusData.status === 'completed') {
          console.log('Payment method change completed successfully');
          setIsChanging(false);
          setProcessingStep('');
          alert('결제수단이 성공적으로 변경되었습니다.');
          await fetchPaymentMethodData(); // 데이터 새로고침
          return;
        } else if (!statusData.success || statusData.status === 'failed') {
          console.log('Payment method change failed:', statusData);
          setIsChanging(false);
          setProcessingStep('');
          alert(statusData.message || '결제수단 변경에 실패했습니다.');
          return;
        } else if (attempts < maxAttempts) {
          console.log(`Payment still pending, retrying in 1 second... (${attempts}/${maxAttempts})`);
          setProcessingStep(`결제수단 변경 처리 중... (${attempts}/${maxAttempts})`);
          setTimeout(pollStatus, 1000); // 1초 후 재시도
        } else {
          console.log('Payment method change polling timed out');
          setIsChanging(false);
          setProcessingStep('');
          alert('결제수단 변경 처리 시간이 초과되었습니다.\n페이지를 새로고침해서 확인해주세요.');
          await fetchPaymentMethodData(); // 데이터 새로고침
        }
      } catch (error) {
        console.error('Status check error:', error);
        if (attempts >= maxAttempts) {
          setIsChanging(false);
          setProcessingStep('');
          alert('결제 상태 확인 중 오류가 발생했습니다.\n페이지를 새로고침해서 확인해주세요.');
          await fetchPaymentMethodData(); // 데이터 새로고침
        } else {
          setTimeout(pollStatus, 1000); // 에러 발생시에도 재시도
        }
      }
    };

    await pollStatus();
  };

  const formatPrice = (amount?: number) => {
    if (!amount) return '0원';
    return `${amount.toLocaleString()}원`;
  };

  const getCardInfo = (billingKey: BillingKey) => {
    // 1. 모델 컬럼에서 가져오기 (우선순위)
    const cardNo = billingKey.card_no_masked;
    const cardCompany = billingKey.card_company;

    // 2. raw_data.verified.card_data에서 가져오기
    const verifiedCardData = billingKey.raw_data?.verified?.card_data;
    const verifiedCardNo = verifiedCardData?.card_no;
    const verifiedCardCompany = verifiedCardData?.card_company;

    return {
      cardNo: cardNo || verifiedCardNo || '',
      cardCompany: cardCompany || verifiedCardCompany || billingKey.raw_data?.card_company || ''
    };
  };

  const formatCardNumber = (cardNo?: string) => {
    if (!cardNo) return '';
    // 앞 4자리와 뒷 4자리 표시: 1234-****-****-5678
    if (cardNo.length >= 8) {
      const first4 = cardNo.slice(0, 4);
      const last4 = cardNo.slice(-4);
      return `${first4}-****-****-${last4}`;
    }
    return `**** **** **** ${cardNo.slice(-4)}`;
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'cancelled':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active': return '활성';
      case 'paused': return '일시정지';
      case 'cancelled': return '해지됨';
      default: return status;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">로딩 중...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {/* 회원 네비게이션 바 */}
        <div className="mb-6">
          <MemberNavigation
            currentPage="payment"
            hasActiveBillingKey={!!data.has_billing_key}
            showTrustedDevicesLink={isMajorAdmin}
          />
        </div>
        
        <div className="bg-white shadow rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h1 className="text-2xl font-bold text-gray-900">결제수단 관리 <small>(사이트내 실시간 결제)</small></h1>
            <p className="mt-1 text-gray-600">정기구독에 사용되는 결제수단을 관리할 수 있습니다. (효성cms 가입은 해당하지 않습니다.)</p>
          </div>

          <div className="p-6 pb-0">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-blue-800">효성cms 안내</h3>
                  <p className="mt-1 text-sm text-blue-700">
                    효성cms 에 대한 결제 서비스 관련 문의나 기타 문의는 <a href='/ask'>[1:1 문의] 게시판</a>에 올려주시기 바랍니다.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <div className="p-6">
            {data?.active_subscription ? (
              <>
                {/* 현재 구독 정보 */}
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">현재 구독 정보</h2>
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <span className="text-sm text-gray-600">구독 상태:</span>
                        <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeClass(data.active_subscription.subscription_status)}`}>
                          {getStatusText(data.active_subscription.subscription_status)}
                        </span>
                      </div>
                      <div>
                        <span className="text-sm text-gray-600">월 결제 금액:</span>
                        <span className="ml-2 font-medium">{formatPrice(data.active_subscription.amount)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* 현재 결제수단 */}
                <div className="mb-8">
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">현재 결제수단</h2>
                  {data.billing_key ? (() => {
                    const cardInfo = getCardInfo(data.billing_key);
                    return (
                      <div className="border border-gray-200 rounded-lg p-4">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-3">
                            <div className="w-10 h-8 bg-blue-100 rounded flex items-center justify-center">
                              <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                                  d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z"></path>
                              </svg>
                            </div>
                            <div>
                              <div className="font-medium text-gray-900">
                                {cardInfo.cardCompany ?
                                  `${cardInfo.cardCompany} 카드` :
                                  (data.billing_key.payment_method === '카드' ? '신용카드' : data.billing_key.payment_method || '신용카드')
                                }
                              </div>
                              {cardInfo.cardNo && (
                                <div className="text-sm text-gray-500">
                                  {formatCardNumber(cardInfo.cardNo)}
                                </div>
                              )}
                              <div className="text-sm text-gray-400">
                                {data.billing_key.pg && `${data.billing_key.pg} 결제`}
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-sm text-gray-500">등록일</div>
                            <div className="font-medium">
                              {new Date(data.billing_key.created_at).toLocaleDateString('ko-KR')}
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })() : (
                    <div className="border border-gray-200 rounded-lg p-4 text-center text-gray-500">
                      등록된 결제수단이 없습니다.
                    </div>
                  )}
                </div>

                {/* 결제수단 변경 */}
                <div>
                  <h2 className="text-lg font-semibold text-gray-900 mb-4">결제수단 변경</h2>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-yellow-800">결제수단 변경 안내</h3>
                        <p className="mt-1 text-sm text-yellow-700">
                          새로운 결제수단으로 1,000원 테스트 결제를 진행한 후(결제 후 잠시후 결제취소됨), 기존 결제수단은 자동으로 해지됩니다.
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
                    <div className="flex">
                      <div className="flex-shrink-0">
                        <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                          <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div className="ml-3">
                        <h3 className="text-sm font-medium text-blue-800">보안 안내</h3>
                        <p className="mt-1 text-sm text-blue-700">
                          저희 서비스는 카드 정보를 직접 저장하지 않습니다. 모든 결제 정보는 PG사를 통해 안전하게 처리되며,
                          결제 완료 후에는 결제 토큰만 보관하여 보안을 보장합니다.
                        </p>
                      </div>
                    </div>
                  </div>

                  <button
                    onClick={handleChangePaymentMethod}
                    disabled={isChanging}
                    className={`py-3 px-4 rounded-lg text-lg font-medium transition-colors ${isChanging
                        ? 'bg-gray-400 cursor-not-allowed text-white'
                        : 'bg-blue-600 hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 text-white'
                      }`}
                  >
                    {isChanging && (
                      <span className="inline-block animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></span>
                    )}
                    {isChanging ? (processingStep || '결제 처리 중... 잠시만 기다려주세요') : '새로운 결제수단 등록'}
                  </button>
                </div>
              </>
            ) : (
              /* 구독이 없는 경우 */
              <div className="text-center py-8">
                <div className="mx-auto w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1"></path>
                  </svg>
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">활성 구독이 없습니다</h3>
                <p className="text-gray-600 mb-6">먼저 멤버십을 구독하신 후 결제수단을 관리하실 수 있습니다.</p>
                <a
                  href="/select_plan"
                  className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700"
                >
                  멤버십 구독하기
                </a>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Loading Modal */}
      {isChanging && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-sm mx-4">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">{processingStep || '처리 중...'}</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
